import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { copiarTexto } from "@/lib/clipboard";
import {
  maskPlaca, placaValida, maskTel, maskCPF, onlyDigits, errosCadastro, textoGanho, textoDescontoAmigo,
  textoOptinPadrao, fmtValidade, linkWhatsappSeguro, erroPublico, segundosAguarde,
  MSG_PAGINA_INDISPONIVEL, MSG_SESSAO_ENCERRADA, OTP_DIGITOS, TEMPO_SEM_USO_CODIGOS_MS, TEMPO_SEM_USO_FORM_MS,
  TEMPO_MAX_OCULTO_MS, COOLDOWN_NOVO_CODIGO_S,
} from "@/lib/indicacaoWeb";
import logo from "@/assets/logo-grupo.png";
import {
  ArrowRight, AlertTriangle, CheckCircle2, Copy, MessageCircle, ShieldCheck, RotateCcw, Smartphone, Car,
} from "lucide-react";

/**
 * Página PÚBLICA da indicação pela web (pedido de 19/10, item 9 — Fase 4): /indicacao/:unidade.
 *
 * Isolada do sistema: usa só publicApi (axios sem os tokens de frentista/Master) e o token curto
 * da página (papel "indicacao_publica") fica só em memória, nunca no navegador. Passos:
 *   placa com cadastro -> códigos na tela (sem código de confirmação; retorno do cliente, 24/10 e
 *     26/10/2026: a partir daí a rota é a MESMA do autocadastro);
 *   placa sem cadastro -> autocadastro -> código (SMS ou WhatsApp, pela rota do Master) -> códigos.
 * Na tela dos códigos: regra de ganho em destaque, cada código com "Encaminhar no WhatsApp"
 * (link wa.me montado pelo servidor, sem número: o cliente escolhe o amigo) e "Gerar códigos"
 * quando permitido. Volta ao início no Concluir ou após 2 minutos sem uso, e os códigos somem. O
 * tempo com a página em segundo plano (no WhatsApp encaminhando um código) não conta, até o limite
 * de 10 minutos oculta (prazo da sessão no servidor).
 * Sem dado pessoal na tela além do primeiro nome e do celular mascarado vindos do servidor.
 */

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let turnstilePromessa = null;

function carregarTurnstile() {
  if (typeof window !== "undefined" && window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstilePromessa) return turnstilePromessa;
  turnstilePromessa = new Promise((ok, falha) => {
    const s = document.createElement("script");
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.defer = true;
    const limite = setTimeout(() => falha(new Error("tempo")), 15000);
    s.onload = () => { clearTimeout(limite); if (window.turnstile) ok(window.turnstile); else falha(new Error("sem turnstile")); };
    s.onerror = () => { clearTimeout(limite); falha(new Error("falha")); };
    document.head.appendChild(s);
  }).catch((e) => { turnstilePromessa = null; throw e; });
  return turnstilePromessa;
}

/** Verificação de segurança (Cloudflare Turnstile) — só existe quando o servidor manda a chave. */
function Turnstile({ siteKey, onToken, resetKey }) {
  const caixa = useRef(null);
  const widget = useRef(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    let vivo = true;
    carregarTurnstile().then((ts) => {
      if (!vivo || !caixa.current) return;
      widget.current = ts.render(caixa.current, {
        sitekey: siteKey,
        language: "pt-br",
        callback: (t) => onToken(t || null),
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    }).catch(() => { if (vivo) setFalhou(true); });
    return () => {
      vivo = false;
      try { if (widget.current != null && window.turnstile) window.turnstile.remove(widget.current); } catch (_) { /* ignora */ }
      widget.current = null;
      onToken(null);
    };
  }, [siteKey, onToken]);
  useEffect(() => {
    if (!resetKey || widget.current == null) return;
    try { window.turnstile.reset(widget.current); } catch (_) { /* ignora */ }
    onToken(null);
  }, [resetKey, onToken]);
  return (
    <div data-testid="ind-captcha">
      <div ref={caixa} />
      {falhou && (
        <p className="text-[12px] text-rose-700 mt-1" data-testid="ind-captcha-falhou">
          Não foi possível carregar a verificação de segurança. Recarregue a página.
        </p>
      )}
    </div>
  );
}

/**
 * Volta ao início após `ms` sem toque, tecla ou rolagem, contando só o tempo com a página VISÍVEL:
 * o link do WhatsApp (e o app de SMS) deixa a página em segundo plano, sem eventos de toque, e esse
 * tempo não pode zerar a tela. Enquanto oculta, a contagem fica parada; ao voltar, recomeça. Oculta
 * por `maxOcultoMs` ou mais (prazo da sessão no servidor), volta ao início assim que reaparecer.
 */
function useSemUso(ativo, ms, aoExpirar, maxOcultoMs = TEMPO_MAX_OCULTO_MS) {
  const cb = useRef(aoExpirar);
  cb.current = aoExpirar;
  useEffect(() => {
    if (!ativo || !(ms > 0)) return undefined;
    let t = null;
    let ocultaDesde = null;
    const oculta = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const armar = () => {
      clearTimeout(t);
      if (ocultaDesde != null) return;           // em segundo plano: não conta
      t = setTimeout(() => cb.current(), ms);
    };
    const pausar = () => {
      if (ocultaDesde == null) ocultaDesde = Date.now();
      clearTimeout(t);
    };
    const retomar = () => {
      if (oculta()) { pausar(); return; }
      const desde = ocultaDesde;
      ocultaDesde = null;
      if (desde != null && maxOcultoMs > 0 && Date.now() - desde >= maxOcultoMs) {
        clearTimeout(t);
        cb.current();
        return;
      }
      armar();
    };
    const aoMudarVisibilidade = () => { if (oculta()) pausar(); else retomar(); };
    const eventos = ["pointerdown", "keydown", "touchstart", "scroll", "input", "mousemove", "click"];
    eventos.forEach((e) => window.addEventListener(e, armar, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", aoMudarVisibilidade);
    window.addEventListener("pagehide", pausar);
    window.addEventListener("pageshow", retomar);
    window.addEventListener("focus", retomar);
    if (oculta()) pausar(); else armar();
    return () => {
      clearTimeout(t);
      eventos.forEach((e) => window.removeEventListener(e, armar, { capture: true }));
      document.removeEventListener("visibilitychange", aoMudarVisibilidade);
      window.removeEventListener("pagehide", pausar);
      window.removeEventListener("pageshow", retomar);
      window.removeEventListener("focus", retomar);
    };
  }, [ativo, ms, maxOcultoMs]);
}

const FORM_VAZIO = { nome: "", cpf: "", telefone: "", codigoInd: "", aceite: false, optin: false };

export default function IndicacaoPublica({
  tempoSemUsoCodigosMs = TEMPO_SEM_USO_CODIGOS_MS,
  tempoSemUsoFormMs = TEMPO_SEM_USO_FORM_MS,
  tempoMaxOcultoMs = TEMPO_MAX_OCULTO_MS,
}) {
  const { unidade } = useParams();
  const slug = String(unidade || "").trim();
  const base = `/public/indicacao/${encodeURIComponent(slug)}`;

  const [cfg, setCfg] = useState(null);
  const [indisponivel, setIndisponivel] = useState(!slug);
  const [semConexao, setSemConexao] = useState(false);
  const [tentativaCfg, setTentativaCfg] = useState(0);

  const [etapa, setEtapa] = useState("placa");    // placa | enviado | cadastro | otp | codigos
  const [enviado, setEnviado] = useState(null);   // {telefone, canal, novos, quantidade} (cliente já cadastrado)
  const [placa, setPlaca] = useState("");
  const [form, setForm] = useState(FORM_VAZIO);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [hp, setHp] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [desafio, setDesafio] = useState(null);   // {id, telefone_mascarado, otp_enviado, origem}
  const [codigoOtp, setCodigoOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [sessao, setSessao] = useState(null);     // {primeiroNome, codigos, podeGerar, motivo, aviso, gerou, smsEnviado, novos, textoGanho}
  const [erro, setErro] = useState("");
  const [info, setInfo] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [copiado, setCopiado] = useState("");

  // Token da página: só em memória (ref), nunca no localStorage/sessionStorage.
  const token = useRef(null);
  // Cada volta ao início invalida as respostas que ainda estavam a caminho.
  const geracao = useRef(0);
  // Pedido em andamento (Enter duplo, clique duplo): trava síncrona, sem esperar o estado renderizar.
  const emEnvio = useRef(false);
  const iniciarEnvio = () => {
    if (emEnvio.current) return false;
    emEnvio.current = true;
    setCarregando(true);
    return true;
  };
  const terminarEnvio = (minha) => {
    if (minha !== geracao.current) return;     // a tela já recomeçou: o novo fluxo controla a trava
    emEnvio.current = false;
    setCarregando(false);
  };

  const precisaCaptcha = !!cfg?.captcha_site_key;
  const onCaptcha = useCallback((t) => setCaptcha(t || null), []);

  useEffect(() => {
    const anterior = document.title;
    document.title = "Indique e ganhe - Grupo Laçador";
    return () => { document.title = anterior; };
  }, []);

  useEffect(() => {
    if (!slug) return undefined;
    let vivo = true;
    setSemConexao(false);
    publicApi.get(`${base}/config`)
      .then((r) => { if (vivo) { setCfg(r.data || {}); setIndisponivel(false); } })
      .catch((e) => {
        if (!vivo) return;
        const er = erroPublico(e);
        // Sem internet (inclusive o 503 offline do service worker) ou servidor fora (5xx): dá para
        // tentar de novo. Só o 404 (página desligada/unidade inexistente) e afins ficam indisponível.
        if (er.tipo === "conexao" || Number(e?.response?.status) >= 500) setSemConexao(true);
        else setIndisponivel(true);
      });
    return () => { vivo = false; };
  }, [slug, base, tentativaCfg]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const encerrarNoServidor = () => {
    const tk = token.current;
    token.current = null;
    if (!tk) return;
    publicApi.post(`${base}/concluir`, {}, { headers: { Authorization: `Bearer ${tk}` } }).catch(() => {});
  };

  const voltarInicio = useCallback((mensagem = "", tipo = "info") => {
    geracao.current += 1;
    emEnvio.current = false;
    encerrarNoServidor();
    setEtapa("placa");
    setPlaca("");
    setForm(FORM_VAZIO);
    setMostrarErros(false);
    setHp("");
    setDesafio(null);
    setEnviado(null);
    setCodigoOtp("");
    setCooldown(0);
    setSessao(null);
    setCopiado("");
    setCarregando(false);
    setErro(tipo === "erro" ? mensagem : "");
    setInfo(tipo === "info" ? mensagem : "");
    setCaptchaReset((n) => n + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  // Ao sair da página (fechar a aba ou trocar de rota), encerra a sessão no servidor.
  useEffect(() => () => { encerrarNoServidor(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sem uso: tela dos códigos em 2 min; formulário/código SMS em 10 min (limpa o que foi digitado).
  const emUso = etapa !== "placa" || !!placa;
  useSemUso(emUso, etapa === "codigos" || etapa === "enviado" ? tempoSemUsoCodigosMs : tempoSemUsoFormMs,
    () => voltarInicio("Por segurança, a tela voltou ao início."), tempoMaxOcultoMs);

  const tratarErro = (e, { manterNaTela = true } = {}) => {
    const er = erroPublico(e);
    if (er.tipo === "indisponivel") { setIndisponivel(true); return er; }
    if (er.tipo === "sessao") { voltarInicio(MSG_SESSAO_ENCERRADA, "erro"); return er; }
    if (manterNaTela) setErro(er.mensagem);
    return er;
  };

  const extras = () => ({
    ...(precisaCaptcha ? { captcha_token: captcha } : {}),
    ...(hp ? { hp } : {}),
  });

  const pedirIdentificacao = async () => {
    const { data } = await publicApi.post(`${base}/identificar`, { placa: maskPlaca(placa), ...extras() });
    return { data };
  };

  const dadosCadastro = () => ({
    nome: form.nome.trim().replace(/\s+/g, " "),
    placa: maskPlaca(placa),
    cpf: onlyDigits(form.cpf),
    telefone: onlyDigits(form.telefone),
    codigo_indicacao: form.codigoInd.trim() ? form.codigoInd.trim().toUpperCase() : null,
    aceite_lgpd: !!form.aceite,
    optin_whatsapp: !!form.optin,
  });

  const pedirCadastro = async () => {
    const { data } = await publicApi.post(`${base}/cadastro`, { ...dadosCadastro(), ...extras() });
    return { data };
  };

  const irParaOtp = (data, origem) => {
    setDesafio({
      id: data.desafio_id, telefone_mascarado: data.telefone_mascarado || "",
      otp_enviado: data.otp_enviado !== false, origem, canal: data.otp_canal || null,
    });
    setCodigoOtp("");
    setCooldown(COOLDOWN_NOVO_CODIGO_S);
    setEtapa("otp");
  };

  // Abre a tela dos códigos com a sessão devolvida pelo servidor: no autocadastro, depois do código;
  // para quem já tem cadastro, logo depois da placa (retorno do cliente, 26/10/2026: a mesma rota).
  const abrirSessao = (data) => {
    token.current = data.token || null;
    setSessao({
      primeiroNome: data.cliente_primeiro_nome || "",
      codigos: Array.isArray(data.codigos_ativos) ? data.codigos_ativos : [],
      podeGerar: !!data.pode_gerar,
      motivo: data.motivo_bloqueio || "",
      aviso: typeof data.aviso === "string" ? data.aviso : "",
      textoGanho: data.texto_ganho || "",
      gerou: false,
      smsEnviado: null,
      novos: [],
    });
    setDesafio(null);
    setCodigoOtp("");
    // Os dados digitados no cadastro não ficam mais na tela.
    setForm(FORM_VAZIO);
    setEtapa("codigos");
  };

  const continuarPlaca = async () => {
    if (emEnvio.current) return;               // Enter/clique repetido enquanto o 1º está a caminho
    setErro(""); setInfo("");
    if (!placaValida(placa)) { setErro("Placa inválida. Confira e digite novamente."); return; }
    if (precisaCaptcha && !captcha) { setErro("Confirme a verificação de segurança."); return; }
    if (!iniciarEnvio()) return;
    const minha = geracao.current;
    try {
      const { data } = await pedirIdentificacao();
      if (minha !== geracao.current) return;
      if (data?.etapa === "codigos" && data.token) {
        abrirSessao(data);
      } else if (data?.etapa === "enviado") {
        // Cliente já cadastrado: os códigos foram para o celular do cadastro (nada deles vem para a tela).
        setEnviado({
          telefone: data.telefone_mascarado || "", canal: data.canal || null,
          novos: data.codigos_novos !== false, quantidade: Number(data.quantidade) || 0,
        });
        setEtapa("enviado");
      } else if (data?.etapa === "otp" && data.desafio_id) irParaOtp(data, "identificar");
      else {
        setMostrarErros(false);
        setEtapa("cadastro");
        setInfo("Não encontramos cadastro com esta placa. Cadastre-se para receber os seus códigos.");
      }
    } catch (e) { if (minha === geracao.current) tratarErro(e); }
    finally {
      terminarEnvio(minha);
      if (precisaCaptcha && minha === geracao.current) setCaptchaReset((n) => n + 1);
    }
  };

  const errosForm = errosCadastro({ ...form, placa, aceiteLgpd: form.aceite });
  const enviarCadastro = async () => {
    if (emEnvio.current) return;
    setErro(""); setInfo("");
    setMostrarErros(true);
    if (Object.keys(errosForm).length) { setErro("Confira os campos destacados."); return; }
    if (precisaCaptcha && !captcha) { setErro("Confirme a verificação de segurança."); return; }
    if (!iniciarEnvio()) return;
    const minha = geracao.current;
    try {
      const { data } = await pedirCadastro();
      if (minha !== geracao.current) return;
      irParaOtp(data, "cadastro");
    } catch (e) { if (minha === geracao.current) tratarErro(e); }
    finally {
      terminarEnvio(minha);
      if (precisaCaptcha && minha === geracao.current) setCaptchaReset((n) => n + 1);
    }
  };

  const novoCodigo = async () => {
    if (emEnvio.current || cooldown > 0 || !desafio) return;
    if (precisaCaptcha && !captcha) { setErro("Confirme a verificação de segurança para pedir um novo código."); return; }
    setErro(""); setInfo("");
    if (!iniciarEnvio()) return;
    const minha = geracao.current;
    try {
      const { data } = desafio.origem === "cadastro" ? await pedirCadastro() : await pedirIdentificacao();
      if (minha !== geracao.current) return;
      if (data?.etapa === "otp" && data.desafio_id) {
        irParaOtp(data, desafio.origem);
        setInfo("Enviamos um novo código. Use sempre o último que chegar.");
      } else {
        voltarInicio("Não foi possível pedir um novo código. Comece de novo.", "erro");
      }
    } catch (e) {
      if (minha !== geracao.current) return;
      const falta = segundosAguarde(e);
      if (falta) setCooldown(falta);
      tratarErro(e);
    } finally {
      terminarEnvio(minha);
      if (precisaCaptcha && minha === geracao.current) setCaptchaReset((n) => n + 1);
    }
  };

  const confirmarOtp = async () => {
    // Enter duplo: o 2º chegaria com o desafio já usado (falha de código contada para o IP no
    // servidor e "Código inválido" logo depois do sucesso).
    if (emEnvio.current || etapa !== "otp" || codigoOtp.length !== OTP_DIGITOS || !desafio) return;
    setErro(""); setInfo("");
    if (!iniciarEnvio()) return;
    const minha = geracao.current;
    try {
      const { data } = await publicApi.post(`${base}/confirmar-otp`, { desafio_id: desafio.id, codigo: codigoOtp });
      if (minha !== geracao.current) return;
      abrirSessao(data);
    } catch (e) {
      if (minha !== geracao.current) return;
      setCodigoOtp("");
      const er = erroPublico(e);
      // Cadastro que caiu depois do código (409): o desafio já foi usado; recomeça com o aviso.
      if (e?.response?.status === 409) { voltarInicio(er.mensagem, "erro"); return; }
      tratarErro(e);
    } finally {
      terminarEnvio(minha);
    }
  };

  const gerarCodigos = async () => {
    if (emEnvio.current || !token.current || !sessao?.podeGerar || sessao?.gerou) return;
    setErro(""); setInfo("");
    if (!iniciarEnvio()) return;
    const minha = geracao.current;
    try {
      const { data } = await publicApi.post(`${base}/gerar-codigos`, {}, {
        headers: { Authorization: `Bearer ${token.current}` },
      });
      if (minha !== geracao.current) return;
      const novos = Array.isArray(data?.codigos) ? data.codigos : [];
      setSessao((s) => ({
        ...s,
        codigos: [...novos, ...(s?.codigos || []).filter((c) => !novos.some((n) => n.codigo === c.codigo))],
        novos: novos.map((n) => n.codigo),
        gerou: true,
        podeGerar: false,
        smsEnviado: typeof data?.sms_enviado === "boolean" ? data.sms_enviado : null,
        canal: data?.canal || null,
        textoGanho: data?.texto_ganho || s?.textoGanho || "",
      }));
    } catch (e) {
      if (minha !== geracao.current) return;
      if (e?.response?.status === 409) {
        const er = erroPublico(e);
        setSessao((s) => ({ ...s, podeGerar: false, motivo: er.mensagem }));
      } else {
        tratarErro(e);
      }
    } finally {
      terminarEnvio(minha);
    }
  };

  const copiar = async (codigo) => {
    const ok = await copiarTexto(codigo);
    setCopiado(ok ? codigo : "");
  };

  const setCampo = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const unidadeNome = cfg?.unidade_nome || "";
  const ganho = textoGanho(cfg?.premio_reais, sessao?.textoGanho || cfg?.texto_ganho);
  const inputCls = "mt-1 w-full h-12 rounded-lg bg-white border px-3.5 text-[15px] outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";
  const borda = (campo) => (mostrarErros && errosForm[campo] ? "border-rose-400" : "border-neutral-200");

  // ---------------------------------------------------------------- telas fora do fluxo
  if (indisponivel || (cfg && cfg.ativo === false)) {
    return (
      <Moldura>
        <div className="card-soft rounded-xl p-5 text-center mt-4" data-testid="ind-indisponivel">
          <AlertTriangle size={22} className="mx-auto text-amber-600" />
          <h1 className="font-serif-display text-xl mt-2">Página indisponível</h1>
          <p className="text-[13px] text-neutral-600 mt-1">{MSG_PAGINA_INDISPONIVEL}</p>
        </div>
      </Moldura>
    );
  }
  if (semConexao) {
    return (
      <Moldura>
        <div className="card-soft rounded-xl p-5 text-center mt-4" data-testid="ind-sem-conexao">
          <AlertTriangle size={22} className="mx-auto text-amber-600" />
          <p className="text-[13px] text-neutral-600 mt-2">Sem conexão com o servidor. Verifique a internet.</p>
          <button type="button" data-testid="ind-tentar-novamente" onClick={() => setTentativaCfg((n) => n + 1)}
                  className="btn-ghost h-11 px-4 mt-3 text-[13px] font-medium">Tentar novamente</button>
        </div>
      </Moldura>
    );
  }
  if (!cfg) {
    return <Moldura><div className="text-center mt-10 text-neutral-500 text-sm" data-testid="ind-carregando">Carregando...</div></Moldura>;
  }

  const honeypot = (
    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
      <label htmlFor="ind-hp-campo">Não preencha este campo</label>
      <input id="ind-hp-campo" data-testid="ind-hp" name="hp" type="text" tabIndex={-1} autoComplete="off"
             value={hp} onChange={(e) => setHp(e.target.value)} />
    </div>
  );

  const avisos = (
    <>
      {erro && (
        <div role="alert" data-testid="ind-erro"
             className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800 flex items-start gap-1.5">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" /><span>{erro}</span>
        </div>
      )}
      {info && (
        <div data-testid="ind-info"
             className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-[13px] text-[color:var(--brand-blue)] flex items-start gap-1.5">
          <CheckCircle2 size={15} className="shrink-0 mt-0.5" /><span>{info}</span>
        </div>
      )}
    </>
  );

  const destaqueGanho = (
    <div data-testid="ind-ganho"
         className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3.5 text-[15px] leading-snug font-semibold text-emerald-900">
      {ganho}
    </div>
  );

  // ---------------------------------------------------------------- 1. placa
  if (etapa === "placa") {
    return (
      <Moldura unidadeNome={unidadeNome}>
        <div className="space-y-3.5 float-in" data-testid="ind-etapa-placa">
          <div>
            <div className="chip mb-2 text-[10px] py-0.5" style={{ borderRadius: 6 }}>Indique e ganhe</div>
            <h1 className="font-serif-display text-2xl leading-tight">Indique amigos e ganhe saldo</h1>
          </div>
          {destaqueGanho}
          <div className="text-[12.5px] text-neutral-600 space-y-1" data-testid="ind-regras">
            {Number(cfg.validade_dias) > 0 && <p>Cada código vale {Number(cfg.validade_dias)} dias.</p>}
            {textoDescontoAmigo(cfg.desconto_amigo_centavos) && <p>{textoDescontoAmigo(cfg.desconto_amigo_centavos)}</p>}
          </div>
          {avisos}
          <div className="card-soft rounded-xl p-4 space-y-3 relative">
            <div>
              <label htmlFor="ind-placa-campo" className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                <Car size={12} /> Placa do seu veículo
              </label>
              <input id="ind-placa-campo" data-testid="ind-placa" value={placa} autoComplete="off" autoCapitalize="characters"
                     onChange={(e) => setPlaca(maskPlaca(e.target.value))}
                     onKeyDown={(e) => { if (e.key === "Enter") continuarPlaca(); }}
                     maxLength={7} placeholder="ABC1D23"
                     className={`${inputCls} border-neutral-200 uppercase font-mono tracking-widest text-lg`} />
            </div>
            {honeypot}
            {precisaCaptcha && <Turnstile siteKey={cfg.captcha_site_key} onToken={onCaptcha} resetKey={captchaReset} />}
            <button type="button" data-testid="ind-continuar" onClick={continuarPlaca}
                    disabled={carregando || !placa || (precisaCaptcha && !captcha)}
                    className="w-full h-12 btn-primary-blue text-[14px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                    style={{ borderRadius: 10 }}>
              {carregando ? "Aguarde..." : (<>Continuar <ArrowRight size={15} /></>)}
            </button>
          </div>
          <div className="text-[12px] text-neutral-500 space-y-1">
            <p><b>Já tem cadastro no posto?</b> Seus códigos aparecem em seguida, prontos para encaminhar.</p>
            <p><b>Ainda não tem?</b> Você faz o cadastro em seguida, em menos de um minuto.</p>
          </div>
        </div>
      </Moldura>
    );
  }

  // ---------------------------------------------------------------- 1b. cliente já cadastrado
  if (etapa === "enviado") {
    const n = enviado?.quantidade || 0;
    const plural = n === 1 ? "código" : "códigos";
    const onde = enviado?.canal === "sms" ? "por SMS para o celular cadastrado" : "para o WhatsApp do celular cadastrado";
    return (
      <Moldura unidadeNome={unidadeNome}>
        <div className="space-y-3.5 float-in" data-testid="ind-etapa-enviado">
          <div>
            <div className="chip mb-2 text-[10px] py-0.5" style={{ borderRadius: 6 }}>Seus códigos</div>
            <h1 className="font-serif-display text-2xl leading-tight">
              {enviado?.canal ? "Pronto! Confira o seu celular" : "Não foi possível enviar agora"}
            </h1>
          </div>
          {enviado?.canal ? (
            <div data-testid="ind-enviado"
                 className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[13px] text-emerald-900 flex items-start gap-1.5">
              <CheckCircle2 size={15} className="shrink-0 mt-0.5" />
              <span>
                {enviado.novos
                  ? `Enviamos ${n} ${plural} de indicação ${onde} `
                  : `Você já tem ${n} ${plural} de indicação ativos. Reenviamos ${n === 1 ? "o seu" : "todos"} ${onde} `}
                <b>{enviado.telefone}</b>. Encaminhe cada código para um amigo.
              </span>
            </div>
          ) : (
            <div data-testid="ind-enviado-falhou"
                 className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 flex items-start gap-1.5">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>
                Seus códigos de indicação estão prontos, mas não conseguimos enviar a mensagem para o celular
                cadastrado <b>{enviado?.telefone || ""}</b>. Tente de novo em alguns minutos ou procure o frentista.
              </span>
            </div>
          )}
          {destaqueGanho}
          {avisos}
          <div className="text-[12px] text-neutral-500 space-y-1" data-testid="ind-enviado-ajuda">
            <p>Por segurança, os códigos não aparecem nesta tela: só o celular do cadastro recebe.</p>
            <p>Trocou de celular? Procure o frentista para atualizar o seu cadastro.</p>
          </div>
          <button type="button" data-testid="ind-concluir" onClick={() => voltarInicio("Pronto! Obrigado por indicar o nosso posto.")}
                  className="w-full h-12 rounded-lg bg-neutral-900 text-white text-[14px] font-medium flex items-center justify-center gap-1.5">
            <RotateCcw size={14} /> Concluir
          </button>
        </div>
      </Moldura>
    );
  }

  // ---------------------------------------------------------------- 2. autocadastro
  if (etapa === "cadastro") {
    const erroCampo = (campo) => (mostrarErros && errosForm[campo]
      ? <p className="text-[11.5px] text-rose-700 mt-1" data-testid={`ind-erro-${campo}`}>{errosForm[campo]}</p> : null);
    return (
      <Moldura unidadeNome={unidadeNome}>
        <div className="space-y-3.5 float-in" data-testid="ind-etapa-cadastro">
          <div>
            <div className="chip mb-2 text-[10px] py-0.5" style={{ borderRadius: 6 }}>Cadastro</div>
            <h1 className="font-serif-display text-2xl leading-tight">Faça o seu cadastro</h1>
            <p className="text-[12.5px] text-neutral-600 mt-1">
              O cadastro só é criado depois que você confirmar o código enviado para o seu celular.
            </p>
          </div>
          {avisos}
          <div className="card-soft rounded-xl p-4 space-y-3 relative">
            <div>
              <label htmlFor="ind-nome-campo" className="text-[10px] uppercase tracking-widest text-neutral-500">Nome completo *</label>
              <input id="ind-nome-campo" data-testid="ind-nome" value={form.nome} autoComplete="name" maxLength={80}
                     onChange={(e) => setCampo("nome", e.target.value)} className={`${inputCls} ${borda("nome")}`} />
              {erroCampo("nome")}
            </div>
            <div>
              <label htmlFor="ind-placa-cad-campo" className="text-[10px] uppercase tracking-widest text-neutral-500">Placa *</label>
              <input id="ind-placa-cad-campo" data-testid="ind-cad-placa" value={placa} maxLength={7} placeholder="ABC1D23"
                     onChange={(e) => setPlaca(maskPlaca(e.target.value))}
                     className={`${inputCls} ${borda("placa")} uppercase font-mono tracking-widest`} />
              {erroCampo("placa")}
            </div>
            <div>
              <label htmlFor="ind-cpf-campo" className="text-[10px] uppercase tracking-widest text-neutral-500">CPF *</label>
              <input id="ind-cpf-campo" data-testid="ind-cpf" value={form.cpf} inputMode="numeric" maxLength={14}
                     placeholder="000.000.000-00" onChange={(e) => setCampo("cpf", maskCPF(e.target.value))}
                     className={`${inputCls} ${borda("cpf")}`} />
              {erroCampo("cpf")}
            </div>
            <div>
              <label htmlFor="ind-tel-campo" className="text-[10px] uppercase tracking-widest text-neutral-500">Celular com DDD *</label>
              <input id="ind-tel-campo" data-testid="ind-telefone" value={form.telefone} inputMode="numeric" maxLength={16}
                     autoComplete="tel-national" placeholder="(51) 99999-9999"
                     onChange={(e) => setCampo("telefone", maskTel(e.target.value))}
                     className={`${inputCls} ${borda("telefone")}`} />
              {erroCampo("telefone")}
            </div>
            <div>
              <label htmlFor="ind-codigo-campo" className="text-[10px] uppercase tracking-widest text-neutral-500">
                Código de indicação (opcional)
              </label>
              <input id="ind-codigo-campo" data-testid="ind-codigo-indicacao" value={form.codigoInd} maxLength={20}
                     autoComplete="off" placeholder="Se um amigo te indicou"
                     onChange={(e) => setCampo("codigoInd", e.target.value.toUpperCase().replace(/\s/g, ""))}
                     className={`${inputCls} border-neutral-200 uppercase`} />
            </div>
            <label className={`flex items-start gap-2 cursor-pointer rounded-lg border p-3 ${mostrarErros && errosForm.aceite ? "border-rose-300 bg-rose-50/60" : "border-neutral-200 bg-neutral-50/60"}`}
                   data-testid="ind-lgpd">
              <input type="checkbox" data-testid="ind-aceite" checked={form.aceite}
                     onChange={(e) => setCampo("aceite", e.target.checked)}
                     className="w-5 h-5 mt-0.5 shrink-0 accent-[color:var(--brand-blue)]" />
              <span className="text-[12px] text-neutral-700 leading-snug">
                <ShieldCheck size={12} className="inline-block -mt-0.5 mr-1 text-[color:var(--brand-blue)]" />
                {cfg.texto_lgpd || "Autorizo o uso dos meus dados no programa de fidelidade e de indicação, de acordo com a LGPD."} *
              </span>
            </label>
            {erroCampo("aceite")}
            <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-neutral-200 bg-neutral-50/60 p-3"
                   data-testid="ind-optin">
              <input type="checkbox" data-testid="ind-optin-check" checked={form.optin}
                     onChange={(e) => setCampo("optin", e.target.checked)}
                     className="w-5 h-5 mt-0.5 shrink-0 accent-emerald-700" />
              <span className="text-[12px] text-neutral-700 leading-snug">
                {cfg.texto_optin || textoOptinPadrao(unidadeNome)} (opcional)
              </span>
            </label>
            {honeypot}
            {precisaCaptcha && <Turnstile siteKey={cfg.captcha_site_key} onToken={onCaptcha} resetKey={captchaReset} />}
            <button type="button" data-testid="ind-cadastrar" onClick={enviarCadastro}
                    disabled={carregando || (precisaCaptcha && !captcha)}
                    className="w-full h-12 btn-primary-blue text-[14px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                    style={{ borderRadius: 10 }}>
              {carregando ? "Aguarde..." : (<>Receber o código <ArrowRight size={15} /></>)}
            </button>
            <button type="button" data-testid="ind-voltar" onClick={() => voltarInicio()}
                    className="w-full btn-ghost h-11 text-[13px] font-medium" style={{ borderRadius: 10 }}>
              Voltar
            </button>
          </div>
        </div>
      </Moldura>
    );
  }

  // ---------------------------------------------------------------- 3. código SMS
  if (etapa === "otp") {
    return (
      <Moldura unidadeNome={unidadeNome}>
        <div className="space-y-3.5 float-in" data-testid="ind-etapa-otp">
          <div>
            <div className="chip mb-2 text-[10px] py-0.5" style={{ borderRadius: 6 }}>Confirmação</div>
            <h1 className="font-serif-display text-2xl leading-tight">Digite o código recebido</h1>
            <p className="text-[13px] text-neutral-600 mt-1 flex items-start gap-1.5" data-testid="ind-otp-destino">
              <Smartphone size={15} className="shrink-0 mt-0.5 text-[color:var(--brand-blue)]" />
              <span>
                Enviamos um código de {OTP_DIGITOS} números{desafio?.canal === "whatsapp" ? " pelo WhatsApp" : desafio?.canal === "sms" ? " por SMS" : ""}{" "}
                para o celular <b>{desafio?.telefone_mascarado || "cadastrado"}</b>.
              </span>
            </p>
          </div>
          {desafio && !desafio.otp_enviado && (
            <div data-testid="ind-otp-nao-enviado"
                 className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900 flex items-start gap-1.5">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>Não foi possível enviar o código agora. Peça um novo código em instantes ou procure o frentista.</span>
            </div>
          )}
          {avisos}
          <div className="card-soft rounded-xl p-4 space-y-3">
            <input data-testid="ind-otp" value={codigoOtp} inputMode="numeric" autoComplete="one-time-code" autoFocus
                   maxLength={OTP_DIGITOS} aria-label="Código recebido"
                   onChange={(e) => setCodigoOtp(onlyDigits(e.target.value).slice(0, OTP_DIGITOS))}
                   onKeyDown={(e) => { if (e.key === "Enter") confirmarOtp(); }}
                   className="w-full h-16 rounded-lg bg-white border border-neutral-200 text-center font-mono text-3xl tracking-[0.5em] outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15" />
            <button type="button" data-testid="ind-confirmar-otp" onClick={confirmarOtp}
                    disabled={carregando || codigoOtp.length !== OTP_DIGITOS}
                    className="w-full h-12 btn-primary-blue text-[14px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                    style={{ borderRadius: 10 }}>
              {carregando ? "Confirmando..." : (<>Confirmar <ArrowRight size={15} /></>)}
            </button>
            {precisaCaptcha && cooldown <= 0 && (
              <Turnstile siteKey={cfg.captcha_site_key} onToken={onCaptcha} resetKey={captchaReset} />
            )}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="ind-novo-codigo" onClick={novoCodigo}
                      disabled={carregando || cooldown > 0 || (precisaCaptcha && !captcha)}
                      className="btn-ghost h-11 text-[12.5px] font-medium disabled:opacity-45" style={{ borderRadius: 10 }}>
                {cooldown > 0 ? `Novo código (${cooldown}s)` : "Pedir novo código"}
              </button>
              <button type="button" data-testid="ind-voltar" onClick={() => voltarInicio()}
                      className="btn-ghost h-11 text-[12.5px] font-medium" style={{ borderRadius: 10 }}>
                Voltar ao início
              </button>
            </div>
          </div>
        </div>
      </Moldura>
    );
  }

  // ---------------------------------------------------------------- 4. códigos
  const codigos = sessao?.codigos || [];
  return (
    <Moldura unidadeNome={unidadeNome}>
      <div className="space-y-3.5 float-in" data-testid="ind-etapa-codigos">
        <div>
          <div className="chip mb-2 text-[10px] py-0.5" style={{ borderRadius: 6 }}>Seus códigos</div>
          <h1 className="font-serif-display text-2xl leading-tight" data-testid="ind-saudacao">
            {sessao?.primeiroNome ? `Olá, ${sessao.primeiroNome}!` : "Olá!"}
          </h1>
        </div>
        {destaqueGanho}
        {sessao?.aviso && (
          <div data-testid="ind-aviso-servidor"
               className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900 flex items-start gap-1.5">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" /><span>{sessao.aviso}</span>
          </div>
        )}
        {avisos}
        {sessao?.gerou && (
          <div data-testid="ind-gerou"
               className={`rounded-lg border px-3 py-2.5 text-[12.5px] flex items-start gap-1.5 ${sessao.smsEnviado === false ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
            {sessao.smsEnviado === false ? <AlertTriangle size={15} className="shrink-0 mt-0.5" /> : <CheckCircle2 size={15} className="shrink-0 mt-0.5" />}
            <span>
              {sessao.smsEnviado === false
                ? "Códigos gerados. Não foi possível enviar para o seu celular agora: encaminhe ou anote os códigos desta tela."
                : sessao.canal === "whatsapp"
                  ? "Códigos gerados e enviados para o seu WhatsApp. Encaminhe cada um para um amigo."
                  : "Códigos gerados e enviados por SMS para o seu celular. Encaminhe cada um para um amigo pelo WhatsApp."}
            </span>
          </div>
        )}

        <div className="space-y-2" data-testid="ind-lista-codigos">
          {codigos.map((c) => {
            const link = linkWhatsappSeguro(c.whatsapp_link);
            const novo = (sessao?.novos || []).includes(c.codigo);
            return (
              <div key={c.codigo} data-testid={`ind-codigo-${c.codigo}`} className="card-soft rounded-xl p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-xl font-bold tracking-widest text-[color:var(--ink)]">{c.codigo}</div>
                    <div className="text-[11.5px] text-neutral-500">
                      {fmtValidade(c.expira_em) ? `Vale até ${fmtValidade(c.expira_em)}` : "Código ativo"}
                      {novo && <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800">Novo</span>}
                    </div>
                  </div>
                  <button type="button" data-testid={`ind-copiar-${c.codigo}`} onClick={() => copiar(c.codigo)}
                          className="btn-ghost h-9 px-2.5 text-[11.5px] font-medium inline-flex items-center gap-1 shrink-0" style={{ borderRadius: 8 }}>
                    <Copy size={12} /> {copiado === c.codigo ? "Copiado" : "Copiar"}
                  </button>
                </div>
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`ind-whatsapp-${c.codigo}`}
                     className="mt-2.5 w-full h-11 rounded-lg text-white text-[13.5px] font-semibold flex items-center justify-center gap-1.5"
                     style={{ background: "#128C4A" }}>
                    <MessageCircle size={15} /> Encaminhar no WhatsApp
                  </a>
                )}
              </div>
            );
          })}
          {codigos.length === 0 && (
            <p className="text-[13px] text-neutral-500 text-center py-2" data-testid="ind-sem-codigos">
              Você ainda não tem códigos de indicação ativos.
            </p>
          )}
        </div>

        {sessao?.podeGerar && !sessao?.gerou && (
          <button type="button" data-testid="ind-gerar" onClick={gerarCodigos} disabled={carregando}
                  className="w-full h-12 btn-primary-blue text-[14px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                  style={{ borderRadius: 10 }}>
            {carregando ? "Gerando..." : (<>Gerar meus 3 códigos <ArrowRight size={15} /></>)}
          </button>
        )}
        {sessao?.motivo && !sessao?.gerou && (
          <div data-testid="ind-motivo"
               className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900 flex items-start gap-1.5">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" /><span>{sessao.motivo}</span>
          </div>
        )}

        <button type="button" data-testid="ind-concluir" onClick={() => voltarInicio("Pronto! Obrigado por indicar o nosso posto.")}
                className="w-full h-12 rounded-lg bg-neutral-900 text-white text-[14px] font-medium flex items-center justify-center gap-1.5">
          <RotateCcw size={14} /> Concluir
        </button>
        <p className="text-[11.5px] text-neutral-500 text-center" data-testid="ind-aviso-tempo">
          Por segurança, esta tela volta ao início sozinha após 2 minutos sem uso e os códigos deixam de aparecer.
          O tempo no WhatsApp encaminhando um código não conta.
        </p>
      </div>
    </Moldura>
  );
}

function Moldura({ unidadeNome, children }) {
  return (
    <div className="min-h-screen flex flex-col" data-testid="indicacao-publica">
      <header className="max-w-md mx-auto w-full px-4 pt-4 pb-2 flex items-center gap-3">
        <div className="bg-white rounded-lg border border-[var(--line)] shadow-sm px-2.5 py-1.5 shrink-0">
          <img src={logo} alt="Grupo Laçador" className="h-9 w-auto object-contain" data-testid="ind-logo" />
        </div>
        <div className="min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-neutral-500">Programa de indicação</div>
          {unidadeNome && <div className="font-serif-display text-[15px] leading-tight truncate" data-testid="ind-unidade">{unidadeNome}</div>}
        </div>
      </header>
      <main className="flex-1 max-w-md mx-auto w-full px-4 pb-10 pt-2">{children}</main>
      <footer className="max-w-md mx-auto w-full px-4 pb-6 text-center text-[11px] text-neutral-400">
        Esta página serve só para cadastro e códigos de indicação. Dúvidas? Fale com o frentista.
      </footer>
    </div>
  );
}
